import { PanelLeftCloseIcon, PanelLeftOpenIcon } from "lucide-react";

import { Icon } from "@/components/atoms/Icon";
import { useDevice } from "@/hooks/useDevice";
import { AppBreadcrumbs } from "@/layouts/AppLayout/AppTopbar/AppBreadcrumbs";
import { AppTopbarEntityPickerPopper } from "@/layouts/AppLayout/AppTopbar/AppTopbarEntityPickerPopper";
import { AppTopbarLoadingSpinner } from "@/layouts/AppLayout/AppTopbar/AppTopbarLoadingSpinner";
import { AppTopbarNotificationsPopper } from "@/layouts/AppLayout/AppTopbar/AppTopbarNotificationsPopper";
import { AppTopbarPanelToggles } from "@/layouts/AppLayout/AppTopbar/AppTopbarPanelToggles";
import { State } from "@/state";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

export function AppTopbarSidebarToggle({
  isSidebarMinimized,
  setIsSidebarMinimized,
  sidebarPanelRef,
  disableOpen,
}: {
  isSidebarMinimized: boolean;
  setIsSidebarMinimized: (isMinimized: boolean) => void;
  sidebarPanelRef: React.RefObject<
    import("react-resizable-panels").PanelImperativeHandle | null
  >;
  disableOpen: boolean;
}) {
  return (
    <Button
      variant="outline"
      size="icon-sm"
      id="sidebar-toggle-button"
      onClick={(e) => {
        if (isSidebarMinimized) {
          setIsSidebarMinimized(false);
        } else {
          e.stopPropagation();
          setIsSidebarMinimized(true);
        }
      }}
      disabled={isSidebarMinimized && disableOpen}
    >
      <Icon
        size="md"
        ReactIcon={isSidebarMinimized ? PanelLeftOpenIcon : PanelLeftCloseIcon}
      />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// <AppTopbar> – Topbar 1: Root navigation bar
//   Sidebar toggle | Breadcrumbs | spacer | utilities | thread toggle
// ---------------------------------------------------------------------------

type AppTopbarProps = {
  sidebarPanelRef: React.RefObject<
    import("react-resizable-panels").PanelImperativeHandle | null
  >;
  disableOpen: boolean;
  forceOpen: boolean;
};

function AppTopbar({
  sidebarPanelRef,
  disableOpen,
  forceOpen,
}: AppTopbarProps) {
  const isSidebarMinimized = State((store) => store.isSidebarMinimized);
  const setIsSidebarMinimized = State((store) => store.setIsSidebarMinimized);
  // A phone - or a window as narrow - has no room for the trail beside the
  // controls; the tab bar says where you are.
  const { isNarrow } = useDevice();

  return (
    <div className="min-w-0 justify-between max-w-full grow flex flex-nowrap pl-2 pr-2 items-center gap-2">
      {/* On a phone the sidebar is under the tab bar's More, and the
          businesses lead the bar instead. */}
      {isNarrow ? (
        <AppTopbarEntityPickerPopper />
      ) : (
        <AppTopbarSidebarToggle
          isSidebarMinimized={isSidebarMinimized}
          setIsSidebarMinimized={setIsSidebarMinimized}
          sidebarPanelRef={sidebarPanelRef}
          disableOpen={disableOpen}
        />
      )}

      <div className="flex-1 min-w-0 overflow-hidden">
        {!isNarrow && <AppBreadcrumbs />}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <AppTopbarLoadingSpinner />

        <AppTopbarNotificationsPopper />

        {!isNarrow && <AppTopbarEntityPickerPopper />}

        {/* A phone opens a panel by what is tapped - a record, a chat - and
            puts it away by its own back; there is no beside to show it in. */}
        {!isNarrow && (
          <AppTopbarPanelToggles disableOpen={disableOpen || forceOpen} />
        )}
      </div>
    </div>
  );
}

export { AppTopbar };
export {
  AppViewFilterRow,
  AppViewSubnav,
  AppViewSubnavAction,
} from "@/layouts/AppLayout/AppTopbar/AppViewSubnav";
